import { Capacitor } from '@capacitor/core';

const getPlugin = () => {
  if (!Capacitor.isNativePlatform()) return null;
  return window?.Capacitor?.Plugins?.CustomerDisplay || null;
};

const safeInvoke = async (method, args) => {
  const plugin = getPlugin();
  if (!plugin || typeof plugin[method] !== 'function') return;
  try {
    await plugin[method](args);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[CustomerDisplayBridge] ${method} failed`, err);
  }
};

const roundCurrency = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const getItemProductId = (item) => String(item?.productId || item?.product || '');

const previewMatchesCart = (previewItems, cart) => {
  if (previewItems.length !== cart.length) return false;

  const unmatchedPreviewItems = [...previewItems];
  return cart.every((cartItem) => {
    const productId = getItemProductId(cartItem);
    const quantity = Number(cartItem?.quantity || 0);
    const matchIndex = unmatchedPreviewItems.findIndex((previewItem) => (
      getItemProductId(previewItem) === productId
      && Number(previewItem?.quantity || 0) === quantity
    ));
    if (matchIndex === -1) return false;
    unmatchedPreviewItems.splice(matchIndex, 1);
    return true;
  });
};

const getPreviewItemForCartItem = (previewItems, cartItem, index) => {
  if (!Array.isArray(previewItems) || previewItems.length === 0) return null;
  const lineId = cartItem?.lineId != null ? String(cartItem.lineId) : '';
  if (lineId) {
    const lineMatch = previewItems.find((item) => String(item?.lineId || '') === lineId);
    if (lineMatch) return lineMatch;
  }

  const productId = getItemProductId(cartItem);
  if (!productId) return null;

  const indexed = previewItems[index];
  if (indexed && getItemProductId(indexed) === productId) return indexed;
  return previewItems.find((item) => getItemProductId(item) === productId) || null;
};

export const buildCustomerDisplaySaleState = ({
  cart = [],
  salePreview = null,
  loyaltyDiscount = 0,
  revision,
} = {}) => {
  if (!Array.isArray(cart) || cart.length === 0) return null;

  const previewItems = Array.isArray(salePreview?.items) ? salePreview.items : [];
  const usePreview = previewMatchesCart(previewItems, cart);
  const items = cart.map((cartItem, index) => {
    const previewItem = usePreview ? getPreviewItemForCartItem(previewItems, cartItem, index) || {} : {};
    const productId = cartItem.productId || previewItem.productId || previewItem.product;
    const lineId = cartItem.lineId || previewItem.lineId || ((productId || 'line') + '-' + index);

    return {
      ...previewItem,
      lineId,
      productId,
      product: previewItem.product || productId,
      name: previewItem.name || cartItem.name,
      price: Number.isFinite(Number(previewItem.price)) ? Number(previewItem.price) : Number(cartItem.price || 0),
      quantity: Number.isFinite(Number(cartItem.quantity)) ? Number(cartItem.quantity) : Number(previewItem.quantity || 0),
      gstApplicable: previewItem.gstApplicable ?? cartItem.gstApplicable,
    };
  });

  const cartTotal = roundCurrency(
    cart.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0)
  );
  const previewTotal = usePreview ? Number(salePreview?.total) : Number.NaN;
  const totalBeforeLoyalty = Number.isFinite(previewTotal) ? previewTotal : cartTotal;
  const total = roundCurrency(totalBeforeLoyalty - Number(loyaltyDiscount || 0));

  return { ...(usePreview ? salePreview : {}), revision, items, total, amountDue: total };
};

export const updateCustomerDisplay = (sale, store) =>
  safeInvoke('updateCart', { sale, store, revision: sale?.revision });

export const showIdleCustomerDisplay = (store, revision) =>
  safeInvoke('showIdle', { store, revision });

export const clearCustomerDisplay = (store, revision) =>
  safeInvoke('clear', { store, revision });
